import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Container, Typography, Box, Stack, Chip, Button, Skeleton, Divider, Card, Grid } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LaunchIcon from '@mui/icons-material/Launch';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { supabase } from '../../lib/supabase';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import EditableImage from '../../components/ui/EditableImage';
import { useBlogPosts } from '../../hooks/useBlogPosts';

const BlogDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'en';

  const { data: allPosts } = useBlogPosts({ isPublished: true, language: currentLang, limit: 10 });
  
  const suggestedPosts = allPosts
    ?.filter(p => p.slug !== slug)
    ?.sort(() => 0.5 - Math.random())
    ?.slice(0, 2) || [];

  const { data: post, isLoading, error } = useQuery({
    queryKey: ['blogPost', slug, currentLang],
    queryFn: async () => {
      // 1. Get the base post
      const { data: basePost, error: postError } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('slug', slug)
        .single();

      if (postError) throw postError;

      // 2. Get the translation
      const { data: translation, error: transError } = await supabase
        .from('blog_posts_translations')
        .select('*')
        .eq('blog_post_id', basePost.id)
        .eq('language_code', currentLang)
        .single();

      // Fallback to English if current language translation is missing
      let finalTranslation = translation;
      if (transError && currentLang !== 'en') {
        const { data: enTranslation } = await supabase
          .from('blog_posts_translations')
          .select('*')
          .eq('blog_post_id', basePost.id)
          .eq('language_code', 'en')
          .single();
        finalTranslation = enTranslation;
      }

      // 3. Get skills
      const { data: skills } = await supabase
        .from('blog_post_skills')
        .select(`
          skill_id,
          skills:skills (
            id,
            translations:skills_translations(name, language_code)
          )
        `)
        .eq('blog_post_id', basePost.id);

      // 4. Get technologies
      const { data: technologies } = await supabase
        .from('blog_post_technologies')
        .select(`
          technology_id,
          technologies:technologies (
            id,
            name
          )
        `)
        .eq('blog_post_id', basePost.id);

      const mappedSkills = skills?.map(s => ({
        id: s.skills.id,
        name: s.skills.translations?.find(t => t.language_code === currentLang)?.name || s.skills.translations?.[0]?.name
      })) || [];

      const mappedTech = technologies?.map(t => ({
        id: t.technologies.id,
        name: t.technologies.name
      })) || [];

      return { ...basePost, ...finalTranslation, skills: mappedSkills, technologies: mappedTech };
    }
  });

  if (isLoading) {
    return (
      <Box sx={{ bgcolor: '#0B0F14', minHeight: '100vh', pt: 15 }}>
        <Container maxWidth="md">
          <Skeleton variant="text" sx={{ fontSize: '4rem', bgcolor: 'rgba(255,255,255,0.1)' }} />
          <Skeleton variant="rectangular" height={400} sx={{ my: 4, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.1)' }} />
          <Skeleton variant="text" sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} count={10} />
        </Container>
      </Box>
    );
  }

  if (error || !post) {
    return (
      <Box sx={{ bgcolor: '#0B0F14', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="error">{t("blogDetail.errors.load")}</Typography>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ bgcolor: '#0B0F14', minHeight: '100vh', pb: 15, pt: { xs: 10, md: 15 } }}>
      <Container maxWidth="md">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/blog')}
            sx={{ color: '#9CA3AF', mb: 4, '&:hover': { color: '#D4AF37' } }}
          >
            {t("blogDetail.backToBlog")}
          </Button>

          <Typography 
            variant="h1" 
            component="div"
            sx={{ 
              color: 'white', 
              mb: 3, 
              fontFamily: 'Philosopher', 
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              lineHeight: 1.2,
              '& h1': { fontSize: 'inherit', margin: 0 },
              '& p': { margin: 0 }
            }}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
              {post.title}
            </ReactMarkdown>
          </Typography>

          <Stack direction="row" spacing={3} alignItems="center" sx={{ mb: 6, flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarTodayIcon sx={{ fontSize: '1rem', color: '#0FA3A3' }} />
              <Typography sx={{ color: '#9CA3AF', fontWeight: 500 }}>
                {new Date(post.published_date || post.created_at).toLocaleDateString(currentLang, { 
                  month: 'long', day: 'numeric', year: 'numeric' 
                })}
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.1)', display: { xs: 'none', sm: 'block' } }} />
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {post.skills?.map(skill => (
                <Chip 
                  key={skill.id} 
                  label={skill.name} 
                  size="small"
                  sx={{ bgcolor: 'rgba(15, 163, 163, 0.1)', color: '#0FA3A3', border: '1px solid rgba(15, 163, 163, 0.2)' }}
                />
              ))}
              {post.technologies?.map(tech => (
                <Chip 
                  key={tech.id} 
                  label={tech.name} 
                  size="small"
                  sx={{ bgcolor: 'rgba(212, 175, 55, 0.1)', color: '#D4AF37', border: '1px solid rgba(212, 175, 55, 0.2)' }}
                />
              ))}
            </Box>
          </Stack>

          {post.cover_image_url && (
            <EditableImage 
              src={post.cover_image_url}
              alt={post.title}
              tableName="blog_posts"
              recordId={post.id}
              imageField="cover_image_url"
              sx={{ 
                width: '100%', 
                height: { xs: '300px', md: '500px' }, 
                borderRadius: '32px',
                mb: 8,
                overflow: 'hidden',
                border: '1px solid rgba(255,255,255,0.1)'
              }}
            />
          )}

          <Box className="prose prose-invert prose-lg max-w-none" sx={{ '& p': { whiteSpace: 'pre-wrap' } }}>
            <ReactMarkdown 
              remarkPlugins={[remarkGfm, remarkBreaks]}
              components={{
                img: ({node, ...props}) => (
                  <Box 
                    component="img" 
                    {...props} 
                    sx={{ 
                      borderRadius: '16px', 
                      my: 4, 
                      maxWidth: '100%',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
                    }} 
                  />
                ),
                h1: ({node, ...props}) => <Typography variant="h3" sx={{ color: 'white', mt: 6, mb: 3, fontFamily: 'Philosopher' }} {...props} />,
                h2: ({node, ...props}) => <Typography variant="h4" sx={{ color: 'white', mt: 6, mb: 3, fontFamily: 'Philosopher' }} {...props} />,
                h3: ({node, ...props}) => <Typography variant="h5" sx={{ color: 'white', mt: 4, mb: 2, fontFamily: 'Philosopher' }} {...props} />,
                p: ({node, ...props}) => <Typography variant="body1" sx={{ color: '#9CA3AF', mb: 3, lineHeight: 1.8, fontSize: '1.125rem' }} {...props} />,
                li: ({node, ...props}) => <Box component="li" sx={{ color: '#9CA3AF', mb: 1, lineHeight: 1.8, fontSize: '1.125rem' }} {...props} />,
                strong: ({node, ...props}) => <Box component="strong" sx={{ color: '#D4AF37', fontWeight: 700 }} {...props} />,
                a: ({node, ...props}) => <Box component="a" sx={{ color: '#0FA3A3', textDecoration: 'underline', '&:hover': { color: '#0D8A8A' } }} {...props} />,
              }}
            >
              {post.content}
            </ReactMarkdown>
          </Box>

          <Divider sx={{ my: 10, borderColor: 'rgba(255,255,255,0.05)' }} />

          {/* Suggested Posts Section */}
          {suggestedPosts.length > 0 && (
            <Box sx={{ mb: 10 }}>
              <Typography variant="h4" sx={{ color: 'white', mb: 5, fontFamily: 'Philosopher', fontWeight: 700 }}>
                {t("blogDetail.suggested.title")}
              </Typography>
              <Grid container spacing={4}>
                {suggestedPosts.map((suggested) => (
                  <Grid item xs={12} md={6} key={suggested.id}>
                    <Card 
                      sx={{ 
                        bgcolor: '#1F2937', 
                        borderRadius: '24px', 
                        border: '1px solid rgba(255,255,255,0.05)',
                        overflow: 'hidden',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: '0.3s',
                        '&:hover': { borderColor: '#0FA3A3', transform: 'translateY(-5px)' }
                      }}
                    >
                      <Box sx={{ height: '200px', position: 'relative' }}>
                        <EditableImage 
                          src={suggested.cover_image_url}
                          alt={suggested.title}
                          tableName="blog_posts"
                          recordId={suggested.id}
                          imageField="cover_image_url"
                          sx={{ height: '100%', width: '100%' }}
                        />
                      </Box>
                      <Box sx={{ p: 4, flexGrow: 1 }}>
                        <Typography variant="h5" sx={{ color: 'white', fontWeight: 700, mb: 2, fontFamily: 'Philosopher' }}>
                          {suggested.title}
                        </Typography>
                        <Button 
                          endIcon={<LaunchIcon />}
                          component={Link}
                          to={`/blog/${suggested.slug}`}
                          sx={{ color: '#0FA3A3', p: 0, fontWeight: 700, '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}
                        >
                          {t("blogDetail.suggested.readMore")}
                        </Button>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Footer CTA Section */}
          <Box sx={{ 
            p: { xs: 6, md: 8 }, 
            background: 'linear-gradient(135deg, #1F2937 0%, #111827 100%)', 
            borderRadius: '32px', 
            textAlign: 'center', 
            border: '1px solid rgba(255,255,255,0.05)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
          }}>
            <Typography variant="h4" sx={{ color: 'white', mb: 3, fontFamily: 'Philosopher', fontWeight: 700 }}>
              {t("blogDetail.footerCta.titlePrefix")} <span style={{ color: '#D4AF37' }}>{t("blogDetail.footerCta.titleHighlight")}</span>
            </Typography>
            <Typography variant="body1" sx={{ color: '#9CA3AF', mb: 5, maxWidth: '700px', mx: 'auto', fontSize: '1.1rem', lineHeight: 1.6 }}>
              {t("blogDetail.footerCta.subtitle")}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="center">
              <Button 
                variant="contained" 
                size="large"
                component="a"
                href="https://calendly.com/sanoualexandreism/30min"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ 
                  bgcolor: '#D4AF37', 
                  color: '#0B0F14', 
                  px: 6, 
                  py: 2, 
                  borderRadius: '12px', 
                  fontWeight: 800,
                  fontSize: '1.1rem',
                  textTransform: 'none',
                  boxShadow: '0 10px 20px rgba(212, 175, 55, 0.2)',
                  '&:hover': { 
                    bgcolor: '#B8962E',
                    transform: 'translateY(-3px)',
                    boxShadow: '0 15px 30px rgba(212, 175, 55, 0.3)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                {t("blogDetail.footerCta.primaryButton")}
              </Button>
              <Button 
                variant="outlined" 
                size="large"
                component={Link}
                to="/services"
                sx={{ 
                  borderColor: 'rgba(255,255,255,0.1)', 
                  color: 'white', 
                  px: 6, 
                  py: 2, 
                  borderRadius: '12px',
                  fontWeight: 800,
                  fontSize: '1.1rem',
                  textTransform: 'none',
                  '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.05)', transform: 'translateY(-3px)' },
                  transition: 'all 0.3s ease'
                }}
              >
                {t("blogDetail.footerCta.secondaryButton")}
              </Button>
            </Stack>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default BlogDetail;
